import { TYPES } from '@/libs/di/types'
import { createPaginationRequestUnixTimeStampSecond } from '@/libs/utilities/createPaginationRequest'
import { SealedWebSocket } from '@/libs/utilities/sealedWebSocket'
import { inject, injectable } from 'inversify'
import {
  parseAnswerMessage,
  parseAnyMessageExcludingReview,
  parseAnyRealtimeMessageExcludingReview,
  parseBotQuestionMessage,
  parseReviewMessage,
  parseSelectionsMessage,
} from './parsers/message'

import type {
  CinnamonClient,
  CinnamonWebSocketFactory,
} from '@/apis/infrastructures'
import type {
  BotQuestionMessageResponse,
  BotQuestionsResponse,
  GetMessageResponse,
  MessageHistoryResponse,
  MessageResponse,
  Question,
  QuestionMessageResponse,
  QuestionsResponse,
  RealtimeMessageResponse,
  StatusMessageResponse,
} from '@/apis/repositories/models/cinnamon'
import type { MessageRepository } from '@/apis/usecases/dataAccesses'
import { isClientError } from '@/libs/utilities/CustomErrors'
import { wait } from '@/libs/utilities/wait'
import type {
  AnyMessage,
  AnyRealtimeMessage,
  Pagination,
  RoomID,
} from '@/types/app'
import { captureException } from '@sentry/vue'

const getMessage = async (
  cinnamon: CinnamonClient,
  roomId: RoomID,
  messageGUID: string,
  entranceGUID: string
) => {
  const { data } = await cinnamon.get<GetMessageResponse>(
    `/rooms/${roomId}/messages/${messageGUID}`,
    {
      ...(entranceGUID.length > 0
        ? {
            headers: {
              '400f-biz-entrance-guid': entranceGUID,
            },
          }
        : {}),
    }
  )

  return data
}

const getQuestionMessage = async (
  cinnamon: CinnamonClient,
  questionMessage: QuestionMessageResponse,
  roomId: RoomID,
  entranceGUID: string
) => {
  const {
    data: { questions },
  } = await cinnamon.get<QuestionsResponse>(`/rooms/${roomId}/questions`, {
    params: {
      message_ids: [questionMessage.id],
      ...(entranceGUID.length > 0
        ? {
            headers: {
              '400f-biz-entrance-guid': entranceGUID,
            },
          }
        : {}),
    },
  })

  // NOTE: cinnamonの/rooms/${roomId}/questionsは、
  // 複数のmessage_idを受け取って複数のquestionsを返す想定だが、現状一つだけしか取ってこないような実装になっている
  return parseSelectionsMessage(questionMessage, questions[0])
}

const getBotQuestionMessage = async (
  cinnamon: CinnamonClient,
  questionMessage: BotQuestionMessageResponse,
  roomId: RoomID,
  entranceGUID: string
) => {
  const {
    data: { questions },
  } = await cinnamon.get<BotQuestionsResponse>(`/rooms/${roomId}/questions`, {
    params: {
      message_ids: [questionMessage.id],
      ...(entranceGUID.length > 0
        ? {
            headers: {
              '400f-biz-entrance-guid': entranceGUID,
            },
          }
        : {}),
    },
  })

  // NOTE: cinnamonの/rooms/${roomId}/questionsは、
  // 複数のmessage_idを受け取って複数のquestionsを返す想定だが、現状一つだけしか取ってこないような実装になっている
  return parseBotQuestionMessage(questionMessage, questions[0])
}

// 呼び出し時にRoomに存在するQuestionsを全件キャッシュして、
// getQuestionMessage + getBotQuestionMessage 相当の単体取得するための関数を返す
// リアルタイム追加されるwsの随時の呼び出しでは非効率なため使わない方がいい
const createQuestionMessageGetters = async (
  cinnamon: CinnamonClient,
  roomId: RoomID,
  entranceGUID: string
) => {
  // params: { message_ids } で指定しないと全件とる
  // https://github.com/400f/cinnamon/blob/35cf2f3f4e93fae8190133cbbbfea0ebf3f54317/models/question.go#L204-L210
  const {
    data: { questions },
  } = await cinnamon.get<QuestionsResponse>(`/rooms/${roomId}/questions`, {
    ...(entranceGUID.length > 0
      ? {
          headers: {
            '400f-biz-entrance-guid': entranceGUID,
          },
        }
      : {}),
  })
  const questionMessageIDMap = questions.reduce(
    (acc, question) => ({
      ...acc,
      [question.message_id]: question,
    }),
    {} as Record<Question['message_id'], Question>
  )

  const getQuestion = (id: number): Question => {
    const response = questionMessageIDMap[id]
    if (response == null) {
      throw new Error(`question message is not found: questionID ${id}`)
    }
    return response
  }

  const execQuestion = (questionMessage: QuestionMessageResponse) => {
    const response = getQuestion(questionMessage.id)
    return parseSelectionsMessage(questionMessage, response)
  }

  const execBotQuestion = (questionMessage: BotQuestionMessageResponse) => {
    const response = getQuestion(questionMessage.id)
    return parseBotQuestionMessage(questionMessage, response)
  }

  return {
    getQuestionMessage: execQuestion,
    getBotQuestionMessage: execBotQuestion,
  }
}

@injectable()
export class MessageRepositoryImpl implements MessageRepository {
  @inject(TYPES.CinnamonClient) private _cinnamonClient: CinnamonClient
  @inject(TYPES.CinnamonWebSocketFactory)
  private _cinnamonWebSocketFactory: CinnamonWebSocketFactory

  public async getRoomMessages(
    roomId: RoomID,
    entranceGUID: string,
    params?: Pagination
  ) {
    const limit = params?.limit ?? 20
    const { data } = await this._cinnamonClient.get<MessageHistoryResponse>(
      `/history/${roomId}`,
      {
        params: createPaginationRequestUnixTimeStampSecond({
          ...(params ?? {}),
          limit: limit + 1,
        }),
        ...(entranceGUID.length > 0
          ? {
              headers: {
                '400f-biz-entrance-guid': entranceGUID,
              },
            }
          : {}),
      }
    )

    const questionMessageGetters = await createQuestionMessageGetters(
      this._cinnamonClient,
      roomId,
      entranceGUID
    )

    const messages = await Promise.all(
      data.messages.map<Promise<AnyMessage>>(message => {
        switch (message.message_type) {
          case 'review':
            return Promise.resolve(parseReviewMessage(message))
          case 'question':
            return Promise.resolve(
              questionMessageGetters.getQuestionMessage(message)
            )
          case 'bot_question':
            return Promise.resolve(
              questionMessageGetters.getBotQuestionMessage(message)
            )
          case 'answer':
            return Promise.resolve(parseAnswerMessage(message))
          default:
            return Promise.resolve(parseAnyMessageExcludingReview(message))
        }
      })
    )
    const sliceArgs =
      (params?.orderby ?? 'asc') === 'asc' ? [-limit] : [0, limit]
    return {
      data: messages.slice(...sliceArgs),
      pageInfo: {
        hasNextPage: messages.length > limit,
      },
    }
  }

  public async getMemberRoomMessages(
    roomId: RoomID,
    entranceGUID: string,
    params?: Pagination
  ) {
    const limit = params?.limit ?? 20
    const { data } = await this._cinnamonClient.get<MessageHistoryResponse>(
      `/history/member/${roomId}`,
      {
        params: createPaginationRequestUnixTimeStampSecond({
          ...(params ?? {}),
          limit: limit + 1,
        }),
        ...(entranceGUID.length > 0
          ? {
              headers: {
                '400f-biz-entrance-guid': entranceGUID,
              },
            }
          : {}),
      }
    )

    const messages = data.messages.map<AnyMessage>(message => {
      switch (message.message_type) {
        case 'review':
          return parseAnyMessageExcludingReview({
            id: message.id,
            guid: message.guid,
            message_type: 'normal',
            message: '評価依頼の送信',
            chat_participant_id: message.chat_participant_id,
            chat_participant_kind: 'planner',
            created_at: message.created_at,
          })
        case 'bot_question':
        case 'question':
          // FIXME: cinnamonのmember responseを考えていないので一旦これでおく
          // これにより、企業メンバのチャットが選択肢付きであった場合、表示されない状態になっている
          return parseAnyMessageExcludingReview({
            id: message.id,
            guid: message.guid,
            message_type: 'normal',
            message: message.message,
            chat_participant_id: message.chat_participant_id,
            chat_participant_kind: message.chat_participant_kind,
            created_at: message.created_at,
          })
        case 'answer':
          return parseAnyMessageExcludingReview(message)
        case 'normal':
          return parseAnyMessageExcludingReview(message)
        case 'livechat':
          return parseAnyMessageExcludingReview(message)
        case 'image':
          return parseAnyMessageExcludingReview(message)
        default:
          return parseAnyMessageExcludingReview(message)
      }
    })

    const sliceArgs =
      (params?.orderby ?? 'asc') === 'asc' ? [-limit] : [0, limit]
    return {
      data: messages.slice(...sliceArgs),
      pageInfo: {
        hasNextPage: messages.length > limit,
      },
    }
  }

  public async updateLatestReadMessage(
    roomId: RoomID,
    entranceGUID: string,
    messageGUID: string
  ) {
    type ReadMessageRequest = {
      message_guid: string
    }

    type UpdateLatestReadMessageResponse = {
      code: number
    }
    const params: ReadMessageRequest = { message_guid: messageGUID }
    const { status } =
      await this._cinnamonClient.post<UpdateLatestReadMessageResponse>(
        `/rooms/${roomId}/latest_read_message`,
        params,
        {
          ...(entranceGUID.length > 0
            ? {
                headers: {
                  '400f-biz-entrance-guid': entranceGUID,
                },
              }
            : {}),
        }
      )
    return { status }
  }

  public async broadcastTypingState(
    socket: SealedWebSocket,
    isTyping: boolean
  ): Promise<void> {
    socket.sendJSON({ message_type: isTyping ? 'typing' : 'deleted' })
  }

  public getMessagingWebSocket(
    roomId: RoomID,
    entranceGUID: string,
    onOpen: (event: Event) => void,
    onMessage: (message: AnyRealtimeMessage) => void,
    onClose: (event: CloseEvent) => void,
    onError: (event: Event) => void
  ): SealedWebSocket {
    const ws = this._cinnamonWebSocketFactory(roomId, entranceGUID)

    const _onMessage = async (event: MessageEvent): Promise<void> => {
      const data = JSON.parse(event.data) as RealtimeMessageResponse

      if (data.message_type === 'new_message') {
        // レプリカラグによるメッセージが取得可能になるまでの遅延を考慮して待つ
        wait(200)

        const message = await getMessage(
          this._cinnamonClient,
          roomId,
          data.message_guid,
          entranceGUID
        )
        handleResponse(message)
        return
      }

      // 移行のための対応。あとで消す
      handleResponse(data)
    }

    const handleResponse = async (
      data: MessageResponse | StatusMessageResponse
    ) => {
      try {
        if (data.message_type === 'review') {
          const message = parseReviewMessage(data)
          onMessage(message)
          return
        }

        if (data.message_type === 'question') {
          const message = await getQuestionMessage(
            this._cinnamonClient,
            data,
            roomId,
            entranceGUID
          )
          onMessage(message)
          return
        }

        if (data.message_type === 'bot_question') {
          const message = await getBotQuestionMessage(
            this._cinnamonClient,
            data,
            roomId,
            entranceGUID
          )
          onMessage(message)
          return
        }

        const anyMessage = parseAnyRealtimeMessageExcludingReview(data)
        onMessage(anyMessage)
      } catch (e) {
        // FIXME: ちゃんとハンドリングしてあげる
        if (!isClientError(e)) {
          captureException(e)
        }
      }
    }

    return new SealedWebSocket(ws, onOpen, _onMessage, onClose, onError)
  }
}
